import dagre from '@dagrejs/dagre';
import {
  type Node,
  type Edge as FlowEdge,
  Position,
  MarkerType,
} from '@xyflow/react';
import type {NexusGraphResponse} from 'src/api/ProactApiClient';
import { NexusVisualState} from 'src/api/ProactApiClient';
import {NODE_WIDTH, NODE_HEIGHT} from './nexusGraphConfig';
import type {NexusFlowNodeData, NexusFlowEdgeData, PhaseJunctionFlowNodeData} from './nexusGraphTypes';

// --- Layout with Dagre ---

import {JUNCTION_SIZE} from './PhaseJunctionNode';

export function layoutGraph(
  nodes: Node[],
  edges: FlowEdge<NexusFlowEdgeData>[]
): {nodes: Node[]; edges: FlowEdge<NexusFlowEdgeData>[]} {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: 50,
    ranksep: 120,
    marginx: 20,
    marginy: 20,
  });

  for (const node of nodes) {
    const isCompact = node.type === 'phaseJunctionNode' || node.type === 'horizonNode';
    g.setNode(node.id, {
      width: isCompact ? JUNCTION_SIZE : NODE_WIDTH,
      height: isCompact ? JUNCTION_SIZE : NODE_HEIGHT,
    });
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  const layoutedNodes = nodes.map(node => {
    const pos = g.node(node.id);
    const isCompact = node.type === 'phaseJunctionNode' || node.type === 'horizonNode';
    const w = isCompact ? JUNCTION_SIZE : NODE_WIDTH;
    const h = isCompact ? JUNCTION_SIZE : NODE_HEIGHT;
    return {
      ...node,
      position: {
        x: pos.x - w / 2,
        y: pos.y - h / 2,
      },
      // Explicit dimensions so MiniMap can render nodes before DOM measurement
      width: w,
      height: h,
    };
  });

  return {nodes: layoutedNodes, edges};
}

// --- Phase edge rewriting ---

export interface RewrittenEdge {
  from: string;
  to: string;
  label?: string;
}

export interface JunctionNodeData {
  id: string;
  mutuallyExclusiveCompletion?: boolean;
}

export interface RewritePhaseResult {
  edges: RewrittenEdge[];
  junctionNodes: JunctionNodeData[];
}

export function rewritePhaseEdges(
  edges: {from: string; to: string; label?: string; phaseName?: string; mutuallyExclusiveCompletion?: boolean}[]
): RewritePhaseResult {
  const passthrough: RewrittenEdge[] = [];
  const bySource = new Map<string, typeof edges>();

  for (const edge of edges) {
    if (edge.phaseName) {
      const existing = bySource.get(edge.from) ?? [];
      existing.push(edge);
      bySource.set(edge.from, existing);
    } else {
      passthrough.push({from: edge.from, to: edge.to, label: edge.label});
    }
  }

  const result = [...passthrough];
  const junctionNodes: JunctionNodeData[] = [];

  for (const [sourceId, sourceEdges] of bySource) {
    const phaseGroups = new Map<string, typeof sourceEdges>();
    for (const edge of sourceEdges) {
      const key = edge.phaseName!;
      const group = phaseGroups.get(key) ?? [];
      group.push(edge);
      phaseGroups.set(key, group);
    }

    const phases = Array.from(phaseGroups.values());

    for (let i = 0; i < phases.length; i++) {
      const group = phases[i];
      const prevPhase = i > 0 ? phases[i - 1] : null;

      if (prevPhase === null) {
        // First phase: edges come from the source node
        for (const edge of group) {
          result.push({from: sourceId, to: edge.to, label: edge.label});
        }
      } else if (prevPhase.length === 1) {
        // Previous phase has a single target: connect directly without junction
        const prevTarget = prevPhase[0].to;
        for (const edge of group) {
          result.push({from: prevTarget, to: edge.to, label: edge.label});
        }
      } else {
        // Previous phase has multiple targets: insert junction node
        const junctionId = `junction:${sourceId}:${i - 1}`;
        const mec = prevPhase[0].mutuallyExclusiveCompletion;
        junctionNodes.push({id: junctionId, mutuallyExclusiveCompletion: mec});

        // All previous phase targets → junction
        for (const prevEdge of prevPhase) {
          result.push({from: prevEdge.to, to: junctionId});
        }

        // Junction → all current phase targets
        for (const edge of group) {
          result.push({from: junctionId, to: edge.to, label: edge.label});
        }
      }
    }
  }

  return {edges: result, junctionNodes};
}

// --- Convert API response to React Flow data ---

export function graphToFlow(
  data: NexusGraphResponse
): {nodes: Node[]; edges: FlowEdge<NexusFlowEdgeData>[]} {
  const entryPoint = data.entryPoint ?? undefined;
  const nodeMap = new Map(data.nodes.map(n => [n.id, n]));

  const flowNodes: Node[] = data.nodes.map(n => ({
    id: n.id,
    type: 'nexusNode',
    position: {x: 0, y: 0},
    data: {
      nexusNode: n,
      isEntryPoint: n.id === entryPoint,
    } satisfies NexusFlowNodeData,
  }));

  const {edges: rewrittenEdges, junctionNodes} = rewritePhaseEdges(
    data.edges.map(e => ({
      from: e.from,
      to: e.to,
      label: e.label ?? undefined,
      phaseName: e.phaseName ?? undefined,
      mutuallyExclusiveCompletion: e.mutuallyExclusiveCompletion ?? undefined,
    }))
  );

  // Append junction nodes
  for (const jn of junctionNodes) {
    flowNodes.push({
      id: jn.id,
      type: 'phaseJunctionNode',
      position: {x: 0, y: 0},
      data: {
        mutuallyExclusiveCompletion: jn.mutuallyExclusiveCompletion,
      } satisfies PhaseJunctionFlowNodeData,
    });
  }

  // Insert horizon nodes for boundary nodes
  const horizonEdges: RewrittenEdge[] = [];
  for (const n of data.nodes) {
    if (n.atHorizon) {
      const horizonId = `horizon:${n.id}`;
      flowNodes.push({
        id: horizonId,
        type: 'horizonNode',
        position: {x: 0, y: 0},
        data: {},
      });
      horizonEdges.push({from: n.id, to: horizonId});
    }
  }

  const allEdges = [...rewrittenEdges, ...horizonEdges];

  const flowEdges: FlowEdge<NexusFlowEdgeData>[] = allEdges.map((e, i) => {
    const isHorizonEdge = e.to.startsWith('horizon:');
    const targetNode = nodeMap.get(e.to);
    const muted = targetNode ? targetNode.visualState === NexusVisualState.Muted : false;
    const edgeColor = isHorizonEdge ? 'var(--mantine-color-gray-4)' : muted ? 'var(--mantine-color-gray-3)' : 'var(--mantine-color-gray-5)';
    return {
      id: `edge-${i}`,
      source: e.from,
      target: e.to,
      type: 'nexusEdge',
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        color: edgeColor,
      },
      data: {
        edgeLabel: e.label,
        muted,
        dashed: isHorizonEdge,
      },
    };
  });

  return layoutGraph(flowNodes, flowEdges);
}
